import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLocale } from '../context/LocaleContext';
import { useTheme } from '../context/ThemeContext';
import SafeIcon from '../common/SafeIcon';
import SocialFooter from '../components/SocialFooter';

export default function AboutPage() {
  const { locale, toggleLocale, t } = useLocale();
  const { isDark, toggleDark } = useTheme();
  const isZh = locale === 'zh-TW';

  return (
    <div className="min-h-screen flex flex-col bg-[#fffbf0] dark:bg-slate-900 transition-colors duration-300">
      <nav className="bg-white/70 dark:bg-slate-900/80 backdrop-blur-lg sticky top-0 z-40 border-b border-gray-100 dark:border-slate-800 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 group font-black text-gray-800 dark:text-white">
            <span className="bg-gradient-to-br from-pink-400 to-rose-400 text-white p-2 rounded-xl shadow-sm rotate-3 group-hover:rotate-6 transition-transform">
              <SafeIcon name="ArrowLeft" className="w-5 h-5" />
            </span>
            {t('nav.home')}
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={toggleLocale} className="w-9 h-9 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400 font-bold text-sm" title={t('nav.toggleLang')}>
              {isZh ? 'EN' : '中'}
            </button>
            <button onClick={toggleDark} className="w-9 h-9 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-yellow-400 flex items-center justify-center" title={t('nav.toggleTheme')}>
              <SafeIcon name={isDark ? 'Moon' : 'Sun'} className="w-5 h-5" />
            </button>
          </div>
        </div>
      </nav>

      <main className="flex-1 w-full max-w-3xl mx-auto p-4 pt-10 md:p-8 md:pt-14">
        <motion.header initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <span className="text-5xl" aria-hidden="true">💗</span>
          <h1 className="text-4xl md:text-5xl font-black text-gray-800 dark:text-white mt-5 mb-4">
            {isZh ? '誰在打造 DaysMatter？' : 'Who Is Behind DaysMatter?'}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 font-medium">
            {isZh ? '認識這個可愛倒數日工具背後的團隊與理念。' : 'Meet the team and purpose behind this delightful countdown tool.'}
          </p>
        </motion.header>

        <div className="space-y-8">
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="hand-drawn-box bg-white dark:bg-slate-800 p-8 md:p-10">
            <h2 className="text-2xl font-black text-gray-800 dark:text-white mb-4">
              {isZh ? 'DaysMatter 為什麼誕生？' : 'Why Was DaysMatter Created?'}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 font-medium leading-relaxed">
              {isZh
                ? 'DaysMatter 由 Homy Tech Limited 開發與營運。我們相信每個值得期待的未來與共同走過的日子都值得被記住，因此打造了簡單、溫暖又可愛的工具，協助你管理紀念日、生日、旅行、考試與工作期限。'
                : 'DaysMatter is developed and operated by Homy Tech Limited. We believe every anticipated moment and shared memory deserves to be remembered, so we built a simple, warm tool for anniversaries, birthdays, trips, exams, and work deadlines.'}
            </p>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="hand-drawn-box bg-white dark:bg-slate-800 p-8 md:p-10">
            <h2 className="text-2xl font-black text-gray-800 dark:text-white mb-4">
              {isZh ? '如何聯絡 DaysMatter 團隊？' : 'How Can You Contact the DaysMatter Team?'}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 font-medium leading-relaxed mb-6">
              {isZh
                ? '如需協助、回報問題、提出功能建議或洽談合作，歡迎直接寫信給我們。'
                : 'For support, bug reports, feature ideas, or partnership enquiries, you are welcome to email us directly.'}
            </p>
            <a href="mailto:info@homy.hk" className="inline-flex items-center gap-3 px-6 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold rounded-xl shadow-lg hover:-translate-y-1 hover:shadow-xl transition-all active:scale-95">
              <SafeIcon name="Mail" className="w-5 h-5" />
              <span>info@homy.hk</span>
            </a>
          </motion.section>
        </div>
      </main>

      <SocialFooter />
    </div>
  );
}